<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import catalogApi from '@/api/catalog'
import { useCatalogStore } from '@/store/catalog'
import { normalizeCatalogError } from '../shared/catalogErrors.mjs'
import CatalogStatus from '../shared/CatalogStatus.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
import PageNavigation from '@/components/navigate/PageNavigation.vue'

const store = useCatalogStore()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const error = ref('')
const deleteTarget = ref(null)
const deleting = ref(false)
const page = ref(Number(route.query.page) || 1)
const filters = reactive({ name: route.query.name || '', code: route.query.code || '', active: route.query.active ?? '' })
const pages = computed(() => Math.max(1, Math.ceil(store.characteristics.count / store.pageSize)))

async function load() {
  loading.value = true; error.value = ''
  try { await store.loadCharacteristics({ page: page.value, limit: store.pageSize, ...filters }) }
  catch (cause) { error.value = normalizeCatalogError(cause).message }
  finally { loading.value = false }
}
async function apply() {
  page.value = 1
  await router.replace({ query: { page: 1, ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')) } })
  await load()
}
async function remove() {
  deleting.value = true; error.value = ''
  try { await catalogApi.deleteCharacteristic(deleteTarget.value.id); deleteTarget.value = null; await load() }
  catch (cause) { error.value = normalizeCatalogError(cause).message }
  finally { deleting.value = false }
}
watch(page, async () => { await router.replace({ query: { ...route.query, page: page.value } }); await load() })
onMounted(load)
</script>

<template>
  <CatalogStatus :loading="loading" :error="error" loading-text="Завантаження характеристик…" @retry="load" />
  <div class="card shadow-sm mb-3"><div class="card-body">
    <form class="row g-3 align-items-end" @submit.prevent="apply">
      <div class="col-md-4"><label class="form-label" for="characteristic-name-filter">Назва</label><input id="characteristic-name-filter" v-model="filters.name" class="form-control"></div>
      <div class="col-md-3"><label class="form-label" for="characteristic-code-filter">Code</label><input id="characteristic-code-filter" v-model="filters.code" class="form-control"></div>
      <div class="col-md-3"><label class="form-label" for="characteristic-active-filter">Статус</label><select id="characteristic-active-filter" v-model="filters.active" class="form-select"><option value="">Усі</option><option :value="true">Активні</option><option :value="false">Неактивні</option></select></div>
      <div class="col-md-2"><button class="btn btn-primary w-100" :disabled="loading"><i class="bi bi-funnel me-2"></i>Застосувати</button></div>
    </form>
  </div></div>
  <div class="d-flex justify-content-between align-items-center mb-3"><h2 class="h4 mb-0">Характеристики</h2><router-link :to="{ name: 'catalogCharacteristicForm', params: { id: 'new' } }" class="btn btn-primary"><i class="bi bi-plus-lg me-2"></i>Додати</router-link></div>
  <div class="table-responsive card shadow-sm"><table class="table table-hover align-middle mb-0"><thead><tr><th>Code</th><th>Назва</th><th>Статус</th><th class="text-end">Дії</th></tr></thead><tbody>
    <tr v-for="item in store.characteristics.items" :key="item.id"><td><code>{{ item.code }}</code></td><td>{{ item.name || '—' }}</td><td><span class="badge" :class="item.active ? 'bg-success' : 'bg-secondary'">{{ item.active ? 'Активна' : 'Неактивна' }}</span></td><td class="text-end"><router-link :to="{ name: 'catalogCharacteristicForm', params: { id: item.id } }" class="btn btn-sm btn-outline-primary me-2" aria-label="Редагувати"><i class="bi bi-pencil"></i></router-link><button class="btn btn-sm btn-outline-danger" type="button" aria-label="Видалити" @click="deleteTarget = item"><i class="bi bi-trash"></i></button></td></tr>
    <tr v-if="!loading && !store.characteristics.items.length"><td colspan="4" class="text-center text-muted py-5">Характеристик не знайдено</td></tr>
  </tbody></table></div>
  <PageNavigation v-model="page" :total-pages="pages" :max-visible="5" />
  <ConfirmDialog :show="!!deleteTarget" title="Видалити характеристику?" :busy="deleting" @cancel="deleteTarget = null" @confirm="remove">Якщо характеристика призначена категорії або має значення товарів, backend заблокує видалення.</ConfirmDialog>
</template>
